import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  AppointmentsType,
  CheckoutParameters,
  CheckoutResponse,
  ExecuteBalancePaymentParams,
  MembershipDetailsInterface,
  MembershipInterface,
  ShopMembership,
  ShopMembershipDetails,
  UpcomingAppointment
} from 'types';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import api from '../api/client.api';

const ACTIVE_LIST = 'ACTIVE_LIST';
const DETAILS = 'DETAILS';
const APPOINTMENTS = 'APPOINTMENTS';

export const userProfileApi = createApi({
  reducerPath: 'userProfileApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  tagTypes: [ACTIVE_LIST, DETAILS, APPOINTMENTS],
  endpoints: (builder) => ({
    getMembershipList: builder.query<ShopMembership[], number>({
      queryFn: async (locid: number) => {
        try {
          const { data } = await api.getMembershipList(locid);

          return { data };
        } catch (error) {
          console.error(`Error while get membership list: ${error.message}`);

          return { error };
        }
      }
    }),
    getMembershipDetails: builder.query<
      ShopMembershipDetails,
      {
        id: number;
        locid: number;
      }
    >({
      queryFn: async (params: { id: number; locid: number }) => {
        try {
          const { data } = await api.getMembershipDetails(params);

          return { data };
        } catch (error) {
          console.error(`Error while get membership details: ${error.message}`);

          return { error };
        }
      }
    }),
    getActiveMemberships: builder.query<MembershipInterface[], string>({
      queryFn: async () => {
        try {
          const { data } = await api.getActiveMemberships();

          return { data };
        } catch (error) {
          console.error(`Error while get memberships list: ${error.message}`);
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ACTIVE_LIST' as const, id })),
              ACTIVE_LIST
            ]
          : [ACTIVE_LIST]
    }),
    getActiveMembershipDetails: builder.query<
      MembershipDetailsInterface,
      string
    >({
      queryFn: async (id: string) => {
        try {
          const { data } = await api.getActiveMembershipDetails(id);

          return { data };
        } catch (error) {
          console.error(`Error while get membership details: ${error.message}`);

          return { error };
        }
      },
      providesTags: [DETAILS]
    }),
    updateMembershipStatus: builder.mutation<
      MembershipDetailsInterface & { success: boolean },
      string
    >({
      queryFn: async (id: string) => {
        try {
          const { data } = await api.updateMembershipStatus(id);

          return { data };
        } catch (error) {
          console.error(`Error while get membership details: ${error.message}`);

          return { error };
        }
      },

      invalidatesTags: [DETAILS]
    }),
    updateMembershipPayment: builder.mutation<
      MembershipDetailsInterface & { success: boolean },
      { id: string; pymtId: number; onSuccess: () => void }
    >({
      queryFn: async ({
        id,
        pymtId,
        onSuccess
      }: {
        id: string;
        pymtId: number;
        onSuccess: () => void;
      }) => {
        try {
          const { data } = await api.updateMembershipPayment(id, pymtId);
          if (data) {
            onSuccess();
          }

          return { data };
        } catch (error) {
          console.error(
            `Error while updating membership payment methods: ${
              error?.response?.data?.error ?? ''
            }`
          );

          return { error: error?.response?.data?.error };
        }
      },

      invalidatesTags: [DETAILS]
    }),
    executeMembershipPayment: builder.mutation<
      MembershipDetailsInterface & { success: boolean; error_text: string },
      number
    >({
      queryFn: async (id: number) => {
        try {
          const { data } = await api.membershipsExecPayment(id);

          return { data };
        } catch (error) {
          console.error(
            `Error while paying membership billing: ${error.message}`
          );

          return { error };
        }
      },
      invalidatesTags: (res) => (res?.success ? [DETAILS] : [])
    }),
    executeBalancePayment: builder.mutation<
      MembershipDetailsInterface & { success: boolean; error: string },
      ExecuteBalancePaymentParams
    >({
      queryFn: async (params: ExecuteBalancePaymentParams) => {
        try {
          const { data } = await api.executeBalancePayment(params);

          return { data };
        } catch (error) {
          console.error(
            `Error while execute Balance Payment: ${error.message}`
          );

          return { error: error.response.data.error ?? error.message };
        }
      }
    }),
    getClAppointments: builder.query<
      UpcomingAppointment[],
      {
        itemType: AppointmentsType;
        number?: number;
      }
    >({
      queryFn: async ({ itemType, number = 10 }) => {
        try {
          const { data } = await api.getClAppointments(itemType, number);

          return { data };
        } catch (error) {
          console.error(`Error fetching appointments: ${error.message}`);

          return { error };
        }
      }
    }),
    cancelAppointment: builder.mutation<
      { success: boolean; message?: string },
      {
        id: number;
        onError?: (message?: string) => void;
        onSuccess?: () => void;
      }
    >({
      queryFn: async ({ id, onSuccess = () => {}, onError = () => {} }) => {
        try {
          const { data } = await api.cancelAppointment(id);
          onSuccess();
          return { data };
        } catch (error) {
          console.error(`Error while cancel appointment: ${error.message}`);
          onError(error.response?.data?.error || error.message);

          return { error: error.response?.data?.error || error.message };
        }
      },
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (response.success) {
          dispatch(
            userProfileApi.util.updateQueryData(
              'getClAppointments',
              { itemType: AppointmentsType.future },
              (draft: UpcomingAppointment[]) => {
                if (draft) {
                  return draft.filter(
                    (appointment: UpcomingAppointment) =>
                      appointment.confirmation_num !== props.id
                  );
                }
                return draft;
              }
            )
          );
        }
      }
    }),
    purchasesMembership: builder.mutation<CheckoutResponse, CheckoutParameters>(
      {
        queryFn: async (params: CheckoutParameters) => {
          try {
            const { data } = await api.cartCompleteCheckout(params);

            return { data };
          } catch (error) {
            console.error(`Error while purchase membership: ${error.message}`);

            return { error: error.message };
          }
        },
        invalidatesTags: (result) => (result?.success ? [ACTIVE_LIST] : [])
      }
    )
  })
});

export const {
  useGetMembershipListQuery,
  useGetActiveMembershipsQuery,
  useGetMembershipDetailsQuery,
  useGetActiveMembershipDetailsQuery,
  useUpdateMembershipStatusMutation,
  useUpdateMembershipPaymentMutation,
  useExecuteMembershipPaymentMutation,
  useExecuteBalancePaymentMutation,
  usePurchasesMembershipMutation,
  useGetClAppointmentsQuery,
  useCancelAppointmentMutation
} = userProfileApi;
